import {
	flexRender,
	getCoreRowModel,
	type OnChangeFn,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { differenceInHours, parseISO } from "date-fns";
import { memo, useMemo } from "react";
import { useDateContext } from "@/components/providers/date-provider";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import getColumns from "./columns";

// * for the core table component
export interface ApptTableProps {
	data: Appointment[];
	// Owned by the page, which persists it and renders the Columns menu — one table per
	// date group means the state cannot live in any single instance.
	columnVisibility?: VisibilityState;
	onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
}

const ApptTable = memo(
	({ data, columnVisibility, onColumnVisibilityChange }: ApptTableProps) => {
		const { tzDate } = useDateContext();
		const columns = useMemo(() => getColumns(), []);
		const table = useReactTable({
			data,
			columns,
			getCoreRowModel: getCoreRowModel(),
			columnResizeMode: "onChange",
			state: { columnVisibility },
			onColumnVisibilityChange,
		});

		return (
			<div className="rounded-md border overflow-x-auto relative">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const isRegNumber = header.id === "registrationNumber";

									return (
										<TableHead
											key={header.id}
											className={cn(
												isRegNumber && "sticky left-0 z-10 bg-background",
											)}
											style={{
												width: header.getSize(),
											}}
										>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => {
								const schedule = row.original;

								// Check if the appointment was created within the last 24 hours
								const isFreshAppt =
									differenceInHours(
										new Date(),
										parseISO(schedule.createdAt, { in: tzDate }),
										{ in: tzDate },
									) <= 24;

								return (
									<TableRow
										key={row.id}
										data-state={row.getIsSelected() && "selected"}
										className={cn({
											"bg-primary/10 hover:bg-primary/20": isFreshAppt,
										})}
									>
										{row.getVisibleCells().map((cell) => {
											const isRegNumber =
												cell.column.id === "registrationNumber";

											return (
												<TableCell
													key={cell.id}
													className={cn(
														isRegNumber && "sticky left-0 z-10 bg-background",
													)}
													style={{
														width: cell.column.getSize(),
													}}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={table.getVisibleFlatColumns().length}
									className="h-24 text-center"
								>
									No results.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		);
	},
);

export default ApptTable;
